import {
  getStepUpToken,
  requestStepUp,
  isStepUpRequired,
  STEP_UP_CANCELLED,
} from '../api/stepUp.js';

const BASE = import.meta.env.VITE_AZURE_FUNCTIONS_URL || '';

function getToken() {
  return localStorage.getItem('grc_auditor_token') || '';
}

async function request(path, { method = 'GET', body, params } = {}) {
  const url = new URL(`${BASE}/api/${path}`);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

  const send = () => {
    const headers = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // §16 (FDNA-02 D2): the in-memory step-up token, when held (../api/stepUp.js).
    const stepUp = getStepUpToken();
    if (stepUp) headers['X-Step-Up-Token'] = stepUp;
    return fetch(url.toString(), {
      method,
      headers,
      ...(body != null ? { body: JSON.stringify(body) } : {}),
    });
  };

  let res = await send();
  // A gated auditor export refused without a step-up: run the shared re-verify
  // dialog, then retry once. A cancel throws — the export did not happen.
  if (await isStepUpRequired(res)) {
    if (!(await requestStepUp())) {
      throw Object.assign(new Error(STEP_UP_CANCELLED), { status: 403, stepUpCancelled: true });
    }
    res = await send();
  }

  if (res.status === 204) return null;
  const json = await res.json().catch(() => ({}));
  if (!res.ok)
    throw Object.assign(new Error(json.error || 'Request failed'), {
      status: res.status,
      body: json,
    });
  return json;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

/**
 * Look up which GRC firms an email address belongs to.
 * Returns { firms: [{ id, name, slug }] }. Public, no JWT required.
 */
export function lookupFirms(email) {
  return request('auditor-lookup', { method: 'POST', body: { email } });
}

export function login({ email, firmSlug, password }) {
  return request('auditor-login', { method: 'POST', body: { email, firmSlug, password } });
}

// ── MFA ───────────────────────────────────────────────────────────────────────

/**
 * Call auditor-mfa-verify with a given action.
 * Actions: 'webauthn-options', 'webauthn-verify', 'totp'
 */
/**
 * §16 auditor step-up (auditor-step-up-verify). Returns the body, or
 * { error, stepUp? } on a refusal — the shape StepUpDialog expects.
 */
export async function stepUpVerify(action, body = {}) {
  try {
    return await request('auditor-step-up-verify', { method: 'POST', body: { action, ...body } });
  } catch (err) {
    return { error: err.message, ...(err.body?.stepUp ? { stepUp: err.body.stepUp } : {}) };
  }
}

/** Start MFA enrolment for a signed-in auditor with no factor (password re-check). */
export async function mfaEnrollStart(password) {
  try {
    return await request('auditor-mfa-enroll', { method: 'POST', body: { password } });
  } catch (err) {
    return { error: err.message };
  }
}

export function mfaVerify(action, body) {
  return request('auditor-mfa-verify', { method: 'POST', body: { action, ...body } });
}

/**
 * Call auditor-mfa-setup with a given action.
 * Actions: 'webauthn-options', 'webauthn-verify', 'totp-generate', 'totp-confirm'
 */
export function mfaSetup(action, body) {
  return request('auditor-mfa-setup', { method: 'POST', body: { action, ...body } });
}

// ── Engagements ───────────────────────────────────────────────────────────────
export function listEngagements({ status } = {}) {
  return request('engagement-list', { params: status ? { status } : undefined });
}

export function getEngagement(engagementId) {
  return request('engagement-read', { params: { engagementId } });
}

export function createEngagement({
  clientName,
  engagementType,
  reference,
  periodStart,
  periodEnd,
  retentionYears,
  templateId,
  frameworkId,
  contactName,
  cvrNumber,
  tryggrSlug,
} = {}) {
  return request('engagement-create', {
    method: 'POST',
    body: {
      clientName,
      engagementType,
      reference,
      periodStart,
      periodEnd,
      retentionYears,
      templateId,
      frameworkId,
      contactName,
      cvrNumber,
      tryggrSlug,
    },
  });
}

export function updateEngagement(engagementId, fields) {
  return request('engagement-update', {
    method: 'POST',
    params: { action: 'update' },
    body: { engagementId, ...fields },
  });
}

/**
 * Engagements are terminated, never deleted (GRC-B-012).
 *
 * The five GRC audit tables carry DENY UPDATE, DELETE and all reference
 * grc_engagements, so the engagement row cannot be removed without destroying
 * the trail — the foreign key blocks it. `action=delete` now returns 409.
 */
export function terminateEngagement(engagementId, reason) {
  return request('engagement-update', {
    method: 'POST',
    params: { action: 'terminate' },
    body: { engagementId, reason },
  });
}

// ── Requests ──────────────────────────────────────────────────────────────────
export async function listRequests(engagementId) {
  const data = await request('evidence-request-read', { params: { engagementId } });
  const sections = data.sections || [];
  const requests = sections.flatMap((s) =>
    (s.requests || []).map((r) => ({ ...r, sectionId: s.id }))
  );
  return { sections, requests, engagement: data.engagement || null };
}

// ── Control annotations ───────────────────────────────────────────────────────
export function readAnnotations(engagementId, prevEngagementId) {
  const params = { engagementId };
  if (prevEngagementId) params.prevEngagementId = prevEngagementId;
  return request('control-annotations-read', { params });
}

export function writeAnnotation(
  engagementId,
  controlRef,
  frameworkId,
  scopeStatus,
  notes,
  internalNotes,
  expectedUpdatedAt
) {
  return request('control-annotations-write', {
    method: 'POST',
    body: {
      engagementId,
      controlRef,
      frameworkId,
      scopeStatus,
      notes,
      internalNotes,
      expectedUpdatedAt: expectedUpdatedAt || null,
    },
  });
}

// ── Firm user management (firm-admin only) ────────────────────────────────────

export function listFirmUsers(firmId) {
  return request('auditor-user-read', { params: { action: 'list', firmId } });
}

export function createFirmUser({ firmId, email, displayName, role, password } = {}) {
  return request('auditor-user-write', {
    method: 'POST',
    params: { action: 'create' },
    body: { firmId, email, displayName, role, ...(password ? { password } : {}) },
  });
}

export function deactivateFirmUser({ userId, firmId } = {}) {
  return request('auditor-user-write', {
    method: 'POST',
    params: { action: 'deactivate' },
    body: { userId, firmId },
  });
}

export function reactivateFirmUser({ userId, firmId } = {}) {
  return request('auditor-user-write', {
    method: 'POST',
    params: { action: 'reactivate' },
    body: { userId, firmId },
  });
}

export function resetFirmUserPassword({ userId, firmId, newPassword } = {}) {
  return request('auditor-user-write', {
    method: 'POST',
    params: { action: 'reset-password' },
    body: { userId, firmId, newPassword },
  });
}

// ── Engagement team (GACA-04) ─────────────────────────────────────────────────

/** Who is currently on an engagement. Pass history to also get ended periods. */
export function readEngagementTeam(engagementId, { history } = {}) {
  return request('engagement-team-read', {
    params: { engagementId, ...(history ? { history: '1' } : {}) },
  });
}

/**
 * The reverse view — every client this auditor can currently reach.
 * This is what answers "who can see this client's data".
 */
export function readAuditorEngagements(userId) {
  return request('engagement-team-read', { params: { userId } });
}

export function addEngagementTeamMember({ engagementId, userId, role } = {}) {
  return request('engagement-team-write', {
    method: 'POST',
    params: { action: 'add' },
    body: { engagementId, userId, role },
  });
}

export function removeEngagementTeamMember({ engagementId, userId } = {}) {
  return request('engagement-team-write', {
    method: 'POST',
    params: { action: 'remove' },
    body: { engagementId, userId },
  });
}

/**
 * Edit the firm's public directory profile and toggle whether it is listed
 * (GACA-05). Firm-admin only, server-enforced. Listing is affirmative — a firm
 * is absent from the directory until `listed: true` is sent.
 */
export function updateFirmDirectory({
  firmId,
  listed,
  description,
  regions,
  frameworks,
  certifications,
  contactEmail,
  website,
} = {}) {
  return request('auditor-firm-write', {
    method: 'POST',
    params: { action: 'directory' },
    body: {
      firmId,
      listed,
      description,
      regions,
      frameworks,
      certifications,
      contactEmail,
      website,
    },
  });
}

// ── Client-initiated invitations (GACA-06) ────────────────────────────────────

/** Invitations addressed to this firm. Scoped server-side to the caller's firm. */
export function readClientInvitations(status) {
  return request('grc-client-invitation-read', { params: status ? { status } : undefined });
}

/**
 * Accept an invitation — creates the engagement, this user as lead auditor,
 * and the consent grant (already granted, because the client's invitation was
 * their consent). Cannot create an invitation; only the client can do that.
 */
export function acceptClientInvitation({ invitationId, engagementType, reference } = {}) {
  return request('grc-client-invitation-respond', {
    method: 'POST',
    params: { action: 'accept' },
    body: { invitationId, engagementType, reference },
  });
}

export function declineClientInvitation({ invitationId, reason } = {}) {
  return request('grc-client-invitation-respond', {
    method: 'POST',
    params: { action: 'decline' },
    body: { invitationId, reason },
  });
}

export function readFirm(firmId) {
  return request('auditor-firm-read', { params: { action: 'get', firmId } });
}

export function updateFirm({ firmId, name, contactEmail, logoUrl } = {}) {
  return request('auditor-firm-write', {
    method: 'POST',
    params: { action: 'update' },
    body: { firmId, name, contactEmail, logoUrl },
  });
}

// ── Export ────────────────────────────────────────────────────────────────────
export function exportAuditor(engagementId) {
  return request('engagement-export-auditor', { method: 'POST', body: { engagementId } });
}

export function exportZip(engagementId) {
  return request('grc-export-zip', { method: 'POST', body: { engagementId } });
}

// ── GRC portal layout ─────────────────────────────────────────────────────────
/**
 * The environment's active layout id, used to decide whether the GRC portal
 * applies the Aurora palette on mount.
 *
 * This used to read `grc_ui_layout`, a field registry-read has never returned
 * and no migration has ever created — so it resolved to null on every call and
 * the palette branch below it was never actually exercised. There is one global
 * layout value per environment (`ui_layout`, set in super-admin → Layouts), and
 * that is the value this was reaching for.
 */
export async function readGrcLayout() {
  const url = new URL(`${BASE}/api/registry-read`);
  const res = await fetch(url.toString());
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  return data?.ui_layout ?? null;
}

// ── Tryggr tenant check ───────────────────────────────────────────────────────
/** Does a Tryggr workspace exist for this slug? Names the tenant it asks about. */
export async function checkTryggrTenant(slug) {
  if (!slug) return null;
  const url = new URL(`${BASE}/api/registry-read`);
  url.searchParams.set('slug', slug);
  const res = await fetch(url.toString());
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  if (!data?.tenants) return null;
  return data.tenants.find((t) => t.slug === slug) || null;
}

const GENERIC_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.dk',
  'hotmail.com',
  'hotmail.dk',
  'hotmail.co.uk',
  'outlook.com',
  'outlook.dk',
  'live.com',
  'live.dk',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'tutanota.com',
  'gmx.com',
  'gmx.net',
  'mail.com',
  'zoho.com',
]);

export function isGenericDomain(email) {
  const domain = email?.split('@')[1]?.toLowerCase();
  if (!domain) return true;
  return GENERIC_DOMAINS.has(domain);
}

/**
 * Find a Tryggr workspace by company name.
 *
 * Runs server-side behind the auditor JWT (grc-tenant-lookup). It used to fetch
 * the whole tenant list from the anonymous registry-read and fuzzy-match in the
 * browser, which made the customer directory searchable without logging in.
 * The matching rules are unchanged.
 */
export async function findTenantByName(name) {
  if (!name || name.length < 2) return null;
  const data = await request('grc-tenant-lookup', {
    method: 'POST',
    body: { name },
  }).catch(() => null);
  return data?.tenant ?? null;
}

/**
 * Find a Tryggr workspace whose administrator uses this email domain.
 *
 * Matching happens server-side because it keys on `adminEmail`, which
 * registry-read trims out of its response — so the browser-side version of this
 * matched against a field that was never present and returned null for every
 * caller. Now behind the auditor JWT, and adminEmail stays server-side.
 */
export async function findTenantByEmailDomain(email) {
  if (!email || isGenericDomain(email)) return null;
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;

  const data = await request('grc-tenant-lookup', {
    method: 'POST',
    body: { emailDomain: domain },
  }).catch(() => null);
  return data?.tenant ?? null;
}

// ── Auditor-client notes (per evidence request) ───────────────────────────────

export function readNotes(requestId) {
  return request('grc-notes-read', { params: { requestId } });
}

export function writeNote({ requestId, engagementId, content }) {
  return request('grc-notes-write', { method: 'POST', body: { requestId, engagementId, content } });
}

// ── Tenant artifact access (linked Tryggr workspace) ─────────────────────────

/** Auditor: request read-only access to a linked Tryggr tenant's artifacts. */
export function requestTenantAccess({ engagementId, requestMessage } = {}) {
  return request('grc-access-request-write', {
    method: 'POST',
    body: { engagementId, requestMessage },
  });
}

/** Auditor: read artifact metadata from the linked tenant (requires granted access). */
export function readTenantArtifacts(engagementId) {
  return request('grc-tenant-artifacts-read', { params: { engagementId } });
}

// ── Tenant invite ─────────────────────────────────────────────────────────────

/**
 * Create a free Tryggr workspace for the engagement's client and record
 * invite_sent_at on the engagement. Returns { ok, slug, inviteSentAt }.
 */
export function inviteTenant(engagementId, { adminEmail, firstName, lastName, displayName } = {}) {
  return request('grc-tenant-invite', {
    method: 'POST',
    body: { engagementId, adminEmail, firstName, lastName, displayName },
  });
}

// ── CVR lookup ────────────────────────────────────────────────────────────────
export async function lookupCvr(cvrNumber) {
  const url = `https://cvrapi.dk/api?vat=${encodeURIComponent(cvrNumber)}&country=dk`;
  const res = await fetch(url, { headers: { 'User-Agent': 'Tryggr GRC/1.0' } });
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  if (!data || data.error) return null;
  return {
    name: data.name || '',
    email: data.email || '',
    website: data.website || '',
    city: data.city || '',
    address: data.address || '',
  };
}
